import i18n from '@/lang/i18n'
import { store } from '@/store/store'
import { FieldFlagsBag, FieldFlags } from 'vee-validate'
import { cloneDeep, isPlainObject } from 'lodash'
import sessionStore from '@/session/sessionStore'
import Quill from 'quill'
import AttachmentResource from '@/api/resources/attachment/attachmentResource'
import { userappProfileImageService } from '@/api/services/common/userapp-profile-image/common-userapp-profile-image.service'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { ModalUtils } from '@/shared/util/modal-utils'
import { CancelToken } from 'axios'
import { ModuleOperation } from '@/api/operations/models/module-operation'
import { StoreUtils } from '@/shared/util/store-utils'
import { StrictNullable } from '@/shared/types/app.types'
import { router } from '@/router'
import { FileUtils } from '@/shared/util/file-utils'
import caseServices from '@/api/resources/common/commonCaseServices'
import clinicalCasePrintService from '@/modules/printModule/services/ClinicalCasePrintService'
import { GetCaseReportPDFParams } from '@/shared/models/common.models'

export class HelperUtils {
    private static get _downloadsUrl(): string {
        // @ts-ignore
        return window.__env ? window.__env.downloadsURL : 'imOnATest!'
    }
    static get anapathPrinterInstallerUrl(): string {
        return `${HelperUtils._downloadsUrl}/AnapathPrinterInstaller.msi`
    }

    private static _getSummaryRefreshPeriod(): number {
        const bubbleRefreshPeriod = store.getters.parameters.global?.bubbleRefreshPeriod
        if (bubbleRefreshPeriod &&
            (bubbleRefreshPeriod === 0 || bubbleRefreshPeriod > 60)) {
            return bubbleRefreshPeriod
        }
        return 60
    }
    static setSummaryInterval(handler: Function): StrictNullable<number> {
        const timeoutInSeconds = HelperUtils._getSummaryRefreshPeriod()
        if (timeoutInSeconds > 0) {
            return setInterval(handler, timeoutInSeconds * 1000)
        } else {
            handler()
            return null
        }
    }

    static removeExternalOptionFromEntityObjects(entity: any): any {
        let mappedEntity = cloneDeep(entity)
        for (const prop in mappedEntity) {
            if (isPlainObject(mappedEntity[prop]) && mappedEntity[prop].hasOwnProperty('externalOption')) {
                delete mappedEntity[prop].externalOption
            }
        }
        return mappedEntity
    }

    static hasModuleAccess(moduleCode: ModuleOperation): boolean {
        const userModules = store.getters.modules.map((module: any) => module.moduleCode)
        return userModules.includes(moduleCode)
    }
    static hasLabOperation(laboratoryId: number, operationCode: string): boolean {
        const laboratory = StoreUtils.laboratories.find(laboratory2 => laboratory2.labId === laboratoryId)
        return !!laboratory?.operationCodes.includes(operationCode)
    }
    static hasOperationInCurrentLaboratory(operationCode: string): boolean {
        return HelperUtils.hasLabOperation(store.getters.labId, operationCode)
    }
    static hasOperationInAnyLaboratory(operationCode: string): boolean {
        return !!sessionStore.state.flatOperationCodes.find((operation: string) => operation === operationCode)
    }
    static hasOperationStartingWith(operationPrefix: string): boolean {
        return !!sessionStore.state.flatOperationCodes.find((operation: string) => operation.startsWith(operationPrefix))
    }
    static hasLocalOperationStartingWith(operationPrefix: string): boolean {
        const foundLaboratory = StoreUtils.laboratories.find(laboratory => laboratory.labId === store.getters.labId)
        return !!foundLaboratory?.operationCodes?.some(operationCode => operationCode.startsWith(operationPrefix))
    }

    static updateSideBarOptionChildren(options: any[], id: number, newOptions: any[]): void {
        let foundOption = options.find(option => option.id === id)
        if (foundOption) {
            foundOption.children = newOptions
        } else {
            options.forEach(option => {
                if (option.hasChildren) {
                    HelperUtils.updateSideBarOptionChildren(option.children, id, newOptions)
                }
            })
        }
    }

    static removeExtensionFromFilename(filename: string): string {
        return filename.replace(/\.[^/.]+$/, '')
    }

    static openUrl(query: any): StrictNullable<Window> {
        return window.open(query.url, '_blank')
    }

    static openPDF(query: any, newWindowTabTitle?: string): void {
        const newWindow = this.openUrl(query)
        if (newWindow) {
            let html = `<body style="height: 100%; width: 100%; overflow: hidden; margin:0; background-color: rgb(82, 86, 89);">
                            <iframe src="${query.url}" style="position:absolute; left: 0; top: 0;" width="100%" height="100%">
                        </body>`
            if (newWindowTabTitle) {
                html = `<head><title>${newWindowTabTitle}</title></head>` + html
            }
            newWindow.onload = () => {
                newWindow.document.write(html)
                newWindow.document.close()
            }
        }
    }

    static someFieldIsDirty(fields: FieldFlagsBag): boolean {
        return Object.values(fields).some((flags: FieldFlags) => flags.dirty)
    }

    static moveInputElementCaretToEnd(elementReference: HTMLInputElement, textLength: number): void {
        elementReference.focus()
        elementReference.setSelectionRange(textLength, textLength)
    }
    static moveQuillEditorCaretToEnd(elementReference: Quill, textLength: number): void {
        elementReference.focus()
        elementReference.setSelection(textLength, textLength)
    }
    static insertTextAtCaretPosition(elementReference: Quill, text: string): void {
        const caretPosition = elementReference.getSelection(true)
        elementReference.insertText(caretPosition.index, text)
    }

    static async getProfileImage(personnelId: number, cancelToken?: CancelToken): Promise<string | undefined | null> {
        const response = await PromiseUtils.toCallSecure(
            userappProfileImageService.getProfileImage(personnelId, cancelToken),
            error => PromiseUtils.handleAxiosError(error, [404]))
        if (response) {
            const attachmentResource = new AttachmentResource()
            return attachmentResource.fileToBase64(response)
        }
        return null
    }

    static translateIfKeyExists(label: string): string {
        return i18n.te(label) ? i18n.tc(label) : label
    }

    static async adviceSaveUiComponent(uiComponent: string, config: any): Promise<any> {
        return PromiseUtils.toCallSecure(
            customConfigResource.save(uiComponent, config),
            () => ModalUtils.showWarningModal(i18n.tc('global.errorUiComponent')))
    }

    static async runActionsWhenChangeLaboratory(goToRoute: {
            route: { name: string },
            postFnToRun: Function
    }): Promise<void> {
        await router.push(goToRoute.route)
        await goToRoute.postFnToRun()
    }

    static async getCaseReportPDF(params: GetCaseReportPDFParams): Promise<void> {
        let includeAssociated = false
        let caseHasAssociatedCases = false
        let blob: StrictNullable<Blob>

        if (params.hasAssociatedCases === undefined) {
            const response = await PromiseUtils.toCallSecure(caseServices.getAssociatedCases(params.laboratoryId, params.caseId))
            if (response) {
                caseHasAssociatedCases = !!response.length
            }
        }

        if (params.hasAssociatedCases || caseHasAssociatedCases) {
            includeAssociated = await ModalUtils.showConfirmationModal(i18n.tc('global.modal.case.viewAssociatedCasesConfirmation'))
        }

        if (params.isClinical) {
            blob = await PromiseUtils.toCallUnique(clinicalCasePrintService.downloadCaseAPReportPdf(params.laboratoryId, params.caseId, includeAssociated))
        } else {
            blob = await PromiseUtils.toCallUnique(caseServices.getCaseAPReport(params.laboratoryId, params.caseId, includeAssociated, params.print))
        }

        if (blob) {
            const openFileParams = {
                blob,
                newWindowTabTitle: `${params.caseLabel} - ${i18n.tc('global.report')}`
            }
            FileUtils.openFile(openFileParams)
        }
    }
}
